import { OnDestroy, OnInit, Input, Type, Directive, ChangeDetectorRef, inject } from '@angular/core';
import { UntypedFormGroup, AbstractControl } from '@angular/forms';
import { ControlListenerPlugin } from '@controls/model/control-listener-plugin';
import { StandardListenerPlugin } from '@controls/core/standard-listener-plugin-template';
import { ControlValidatorPlugin } from '@controls/model/control-validator-plugin';
import { FormControlMaker } from '@controls/model/form-control-maker';
import { ComponentPlugin, FormRegisterableComponent } from '@controls/model/form-registerable-component';
import { DecoratorServiceProvider } from '@core/utils/decorator-service-provider';
import { DataObject } from '@shared/models/data-object.model';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * Generic Abstract component base class of type C (where C can be GroupSection, Section or Column from @miq/columns)
 * to receive config object & the main/global form as input. It leaves open the data gathering and usage of Store is
 * expected to do so.
 *
 * Using store to initialize the data$ observable opens the chance be flexible in the way the component work in order to
 * simplify its work of getting the data that matters for the component.
 */
@UntilDestroy()
@Directive()
export abstract class AbstractFormRegisterableComponentTemplate<C>
  implements FormRegisterableComponent<C>, OnDestroy, OnInit
{
  /**
   * Provides behind the scenes the change detector ref in case components are developed with OnPush strategy
   */
  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected logger: CbreLogger = inject(CbreLoggerToken);

  /** Represents GroupSection Section or Column object */
  protected _config: C;
  configChange?: any;
  public get config(): C {
    return this._config;
  }

  @Input() public set config(config: C) {
    const _shouldEmit = this._config != null;
    this._config = config;
    if (_shouldEmit) {
      // emit the change
      this.configChange?.emit(config);
      // mark it for check
      this.cdr?.markForCheck();
    }
  }

  /**
   * Main form
   */
  @Input() public form: UntypedFormGroup = null;

  protected _data: DataObject = {};
  @Input() set data(data: DataObject) {
    this._data = data;
  }
  /**
   *  Main form getter
   */
  get data() {
    return this._data;
  }

  /**
   * Record or slice of the full record that will be manipulated in the component.
   */
  public data$: Observable<any>;

  /**
   * Component form control (can be either FormGroup, FormArray or FormControl) depending on where the component is
   * being used
   */
  public control: AbstractControl;

  /**
   * any observable created in this class or child classes can push the subscriptions here and when the component
   * destroys, will loop through the subscriptions and call unsubscribe for each, to avoid memory leaks
   */
  protected listenerSubscription: Map<
    AbstractControl,
    Set<{ listener: StandardListenerPlugin<C>; subs: Subscription }>
  > = new Map();

  /**
   * Final instance for FormControlMaker. if configured as controlMaker type in catalog config
   * it takes precedence over the injected one, if none component uses the provided service
   */
  protected controlMakerService: FormControlMaker<any>;

  /**
   * Final instance for ControlValidatorPlugin. if configured as controlValidatorPlugin type in catalog config
   * it takes precedence over the injected one, if none component uses the provided service
   */
  protected controlValidatorPluginService: ControlValidatorPlugin<C>;

  constructor(
    protected controlBuilder: FormControlMaker,
    protected controlValidatorPlugin: ControlValidatorPlugin<C>,
    protected listenerPlugins?: Array<ControlListenerPlugin<C>> // Optional
  ) {}

  ngOnInit(): void {
    this.controlMakerService = this._getControlBuilder();
    this.controlValidatorPluginService = this._getControlValidatorPlugin();
    this.resolveDataAndCreateControls();
  }

  /**
   * Returns configured controlMaker service if configured, else it uses the injected
   * @param config
   * @returns
   */
  protected _getControlBuilder() {
    const ControlMakerType = (this.config as C & { controlMaker?: Type<FormControlMaker<C>> })?.controlMaker;
    return ControlMakerType != null
      ? this._resolveInjectable<FormControlMaker<C>>(ControlMakerType)
      : this.controlBuilder;
  }

  /**
   * Returns configured validatorPlugin service if configured, else it uses the injected
   * @param config
   * @returns
   */
  protected _getControlValidatorPlugin() {
    const ControlValidatorPluginType = (this.config as C & { validatorPlugin?: Type<ControlValidatorPlugin<C>> })
      ?.validatorPlugin;
    return ControlValidatorPluginType != null
      ? this._resolveInjectable(ControlValidatorPluginType)
      : this.controlValidatorPlugin; // service injected
  }

  protected _resolveInjectable<T>(_Class: Type<T>) {
    return DecoratorServiceProvider.getInjector().get<T>(_Class);
  }

  /**
   * Set's the data$ observable from selector or an of(data) if data was provided as input
   * else it resolves from dataSelector$ implementation
   */
  protected resolveDataAndCreateControls(): void {
    if (this.config == null) {
      // prevent component creation hook to continue if config is null
      return;
    }
    const data$ = of(this.data);
    this.data$ = this.dataSelector$(data$).pipe(tap(data => this._onDataResolved(data)));
  }

  /**
   * Each child has to implement their own way to get the date they are supposed to work on
   */
  dataSelector$(data?: Observable<DataObject>): Observable<any> {
    return data;
  }

  /**
   * Hook to execute registerControl after given data has been resolved
   */
  protected _onDataResolved(data: DataObject): void {
    this.registerControl(this.config, this.form, data);
    this.afterRegisterControl(data);
  }

  /**
   * Uses the controlBuilder service to create and attache the component to the main form
   * @param config
   * @param form
   * @param data
   */
  registerControl(config: C, form: UntypedFormGroup, data: DataObject): AbstractControl {
    // create control
    this.control = this._buildControl(config, form, data);
    // set up validators
    this.setUpValidators(config, this.control);
    // set up listeners (if any)
    this.setUpListeners(config, this.control, form);

    return this.control;
  }

  /**
   * Uses derived controlMakerService to build a control with given config, form and data
   * @param config
   * @param form
   * @param data
   * @returns
   */
  protected _buildControl(config: C, form: UntypedFormGroup, data: DataObject) {
    return this.controlMakerService.makeControlEntry(config, form, data);
  }

  /**
   * Post registerControl component processing (used to add hooks after control has been set up)
   * @param _data
   */
  protected afterRegisterControl(_data?: DataObject) {
    // mark for check the component on value changes
    this.tickOnValueChanges(this.control);
    this.connectComponentPlugin();
  }

  protected isControlRebuilt = new Subject();
  protected tickOnValueChanges = (control: AbstractControl) => {
    this.isControlRebuilt.next(true);
    control.valueChanges
      .pipe(untilDestroyed(this), takeUntil(this.isControlRebuilt.asObservable()))
      .subscribe(() => this.cdr.markForCheck());
  };

  protected connectComponentPlugin() {
    const componentPlugin = (this.config as C & { ComponentPlugin: ComponentPlugin<C> }).ComponentPlugin;
    if (componentPlugin != null) {
      try {
        const plugin = this._resolveInjectable(componentPlugin.Type);
        const pluginTick$ = plugin.configure(this, componentPlugin.options);
        if (pluginTick$ instanceof Observable) {
          pluginTick$.pipe(untilDestroyed(this)).subscribe(this.onComponentPluginConnected);
        }
      } catch (e) {
        this.logger.warn(`Unable to plug ${componentPlugin} plugin due to: ${e.message}`);
      }
    }
  }

  protected onComponentPluginConnected = (_next: any) => {
    // empty on purpose, can override to provide specific behavior
  };

  /**
   * Uses injected validator and executes connection
   * @param config
   * @param control
   */
  setUpValidators(config: C, control: AbstractControl): void {
    this.controlValidatorPluginService.connect(...[{ control, config }]);
  }

  /**
   * Categorize listener service by class instance and executes pertinent method
   * to set up each listener type
   * @param config
   * @param control
   * @param globalForm
   */
  setUpListeners(config: C, control: AbstractControl, globalForm: UntypedFormGroup) {
    this.injectConfigureListeners(config as C & { controlListener: Type<ControlListenerPlugin<C>> });
    if (config != null && this.areThereListenersConfigured) {
      const standardListeners = this.listenerPlugins.filter(
        listener => listener instanceof StandardListenerPlugin
      ) as StandardListenerPlugin<C>[];
      this._setUpStandardListenerPlugins(standardListeners, config, control, globalForm);
    }
  }

  protected injectConfigureListeners(config: C & { controlListener?: Type<ControlListenerPlugin<C>> }) {
    if (config?.controlListener != null) {
      if (this.listenerPlugins != null && Array.isArray(this.listenerPlugins)) {
        const doesListernerExists = this.listenerPlugins.some(listner => listner instanceof config.controlListener);
        if (!doesListernerExists) {
          const listerInstance = this._resolveInjectable(config.controlListener);
          this.listenerPlugins.push(listerInstance);
        }
      } else if (this.listenerPlugins == null) {
        const listerInstance = this._resolveInjectable(config.controlListener);
        this.listenerPlugins = [listerInstance];
      }
    }
  }

  /**
   * Returns boolean weather existence of listener services are injected or not
   */
  protected get areThereListenersConfigured() {
    const ignoreListeners = (this.config as C & { componentConfig: DataObject }).componentConfig?.ignoreListeners;
    return this.listenerPlugins && this.listenerPlugins.length > 0 && !ignoreListeners;
  }

  /**
   * Standard listeners are services who generates their onw observable based on
   * the full context of the control (config, control, & global form).
   * This type of service has to be subscribed here and save their subscription
   * so on destroy can be destroyed
   *
   * @param streamListeners
   * @param config
   * @param control
   * @param globalForm
   */
  protected _setUpStandardListenerPlugins(
    streamListeners: Array<StandardListenerPlugin<C>>,
    config: C,
    control: AbstractControl,
    globalForm: UntypedFormGroup
  ) {
    streamListeners
      .filter(listener => !listener.isConnected(control))
      .forEach(listener => {
        const connected$ = listener.connect(config, control, globalForm);
        const currentConnections = this.listenerSubscription.get(control);
        if (currentConnections == null) {
          const listenerSet = new Set<{ listener: StandardListenerPlugin<C>; subs: Subscription }>([
            { listener, subs: connected$.subscribe() },
          ]);
          this.listenerSubscription.set(control, listenerSet);
        } else {
          currentConnections.add({ listener, subs: connected$.subscribe() });
        }
      });
  }

  ngOnDestroy(): void {
    this._disconnectValidatorsAndListeners();
  }

  /**
   * Hook to clean up listeners and validation plugs
   */
  protected _disconnectValidatorsAndListeners() {
    if (this.listenerSubscription?.size) this._disconnectListeners();

    // disconnect validator, if provided
    if (this.controlValidatorPluginService?.disconnect != null && this.control != null && this.config != null) {
      this.controlValidatorPluginService.disconnect({ control: this.control, config: this.config });
    }
  }

  protected _disconnectListeners() {
    this.listenerSubscription.forEach((listenerSubs, control) => {
      listenerSubs.forEach(({ listener, subs }) => {
        subs.unsubscribe();
        listener.disconnect(control);
      });
      listenerSubs.clear();
    });
    this.listenerSubscription.clear();
  }
}
